import React, { useState, useEffect } from "react";



import { Link } from "react-router-dom";
import db from "./Firebase";
import Swal from "sweetalert2";
import { collection, query, where, getDocs, updateDoc, doc, Timestamp } from "firebase/firestore";
import Moment from "moment";
import { extendMoment } from "moment-range";
import { Button } from "react-bootstrap";
import * as XLSX from "xlsx"; // ✅ Import XLSX for Excel export

function MembershipTracking() {
    const moment = extendMoment(Moment);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [userTypes, setUserTypes] = useState({}); // ✅ Stores precomputed "New" or "Old" values
    const [personalDetails, setPersonalDetails] = useState({});



    const extendExpiredMembershipsToApril30 = async () => {
        const now = new Date();
        let april30 = new Date(now.getFullYear(), 3, 30); // April is month 3 (0-indexed)

        if (now > april30) {
            april30 = new Date(now.getFullYear() + 1, 3, 30);
        }

        try {
            const snapshot = await getDocs(collection(db, "nv_glow_membership"));
            const updates = [];

            snapshot.docs.forEach((docSnap) => {
                const data = docSnap.data();
                const endDate = data.end_date?.toDate?.() || new Date(data.end_date);

                if (endDate < now) {
                    updates.push(
                        updateDoc(doc(db, "nv_glow_membership", docSnap.id), {
                            end_date: Timestamp.fromDate(april30),
                        })
                    );
                }
            });

            await Promise.all(updates);
            console.log("✅ Expired memberships updated to:", april30.toDateString());
        } catch (error) {
            console.error("❌ Error updating expired memberships:", error);
        }
    };


    useEffect(() => {
        const unsubscribe = db
            .collection("nv_glow_membership")
            .orderBy("start_date", "desc")
            .onSnapshot((snapshot) => {
                const memberships = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    data: doc.data(),
                }));
                setData(memberships);
                setFilteredData(memberships);
                fetchPersonalDetails(memberships); // Fetch user details
            });

        return () => unsubscribe();
    }, []);

    // **Fetch personal details for all phone numbers**
    const fetchPersonalDetails = async (memberships) => {
        const phoneNumbers = [...new Set(memberships.map(({ data }) => data.phone_no))];

        if (phoneNumbers.length === 0) return;

        let userTypeMap = {};
        let detailsMap = {};
        const batchSize = 10; // Firestore 'in' query limit
        const cutoffDate = new Date("2025-01-27");

        // 🔥 Fetch all batches in parallel
        const batchQueries = [];
        for (let i = 0; i < phoneNumbers.length; i += batchSize) {
            const batch = phoneNumbers.slice(i, i + batchSize);
            batchQueries.push(getDocs(query(collection(db, "personal_details"), where("phone_no", "in", batch))));
        }

        // 🔥 Wait for all Firestore queries to complete simultaneously
        const snapshots = await Promise.all(batchQueries);

        // 🔍 Process results from all snapshots
        snapshots.forEach((snapshot) => {
            snapshot.docs.forEach((doc) => {
                const d = doc.data();
                detailsMap[d.phone_no] = d.full_name || "N/A"; // Default if missing

                if (d.created_date) {
                    const userCreatedDate = d.created_date.toDate ? d.created_date.toDate() : new Date(d.created_date);
                    userTypeMap[d.phone_no] = userCreatedDate > cutoffDate ? "New" : "Old";
                } else {
                    userTypeMap[d.phone_no] = "N/A";
                }
            });
        });

        // 🔥 Update state in one go (minimizing re-renders)
        setPersonalDetails(detailsMap);
        setUserTypes(userTypeMap);
    };


    // **Export to Excel**
    const exportToExcel = () => {
        const formattedData = displayedData
            .filter(({ data }) => data.phone_no && data.phone_no.length === 10) // ✅ Only include valid phone numbers
            .map(({ data }) => ({
                "Phone No": data.phone_no,
                "Full Name": personalDetails[data.phone_no] || "N/A", // ✅ Include names
                "Membership Type": data.membership_type,
                "Price": `$${data.price}`,
                "Start Date": moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a"),
                "End Date": moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a"),
                "Status": moment(data.end_date.toDate()).isAfter(moment()) ? "Active" : "Expired",
                "User Type": userTypes[data.phone_no] || "N/A", // ✅ Include New/Old status
            }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Memberships");
        XLSX.writeFile(wb, "MembershipData.xlsx");
    };


    // **Filter Data Based on Button Click**
    const handleFilter = (type) => {
        setFilterType(type);
        const now = moment();

        let filtered;
        if (type === "active") {
            filtered = data.filter((record) => moment(record.data.end_date.toDate()).isAfter(now));
        } else if (type === "expired") {
            filtered = data.filter((record) => moment(record.data.end_date.toDate()).isBefore(now));
        } else {
            filtered = data;
        }

        setFilteredData(filtered);
    };


    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This record will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                db.collection("nv_glow_membership").doc(id).delete()
                    .then(() => {
                        Swal.fire("Deleted!", "The record has been deleted.", "success");
                    })
                    .catch((error) => {
                        Swal.fire("Error!", "Something went wrong.", "error");
                    });
            }
        });
    };

    // **Filter data by search query**
    const displayedData = filteredData.filter((record) =>
        record.data.phone_no.includes(searchQuery)
    );

    return (
        <>
            <div className="p-2 d-flex justify-content-between">
                <Link to="/">
                    <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
                </Link>
            </div>

            <div className="p-3">
                <div className="card-header pb-0 px-3">
                    <h6 className="mb-2">
                        Membership Tracking Page - | {data.length}
                        {/* (Count: {displayedData.filter(({ data }) => data.phone_no && data.phone_no.length === 10).length}) */}
                    </h6>
                </div>

                {/* Filter Buttons */}
                <div className="mb-3 mt-3 d-flex gap-2">
                    <Button variant="success" onClick={exportToExcel}>📥 Download Excel</Button>
                    <Button variant={filterType === "all" ? "primary" : "outline-primary"} onClick={() => handleFilter("all")}>
                        All
                    </Button>
                    <Button variant={filterType === "active" ? "success" : "outline-success"} onClick={() => handleFilter("active")}>
                        Active
                    </Button>
                    <Button variant={filterType === "expired" ? "danger" : "outline-danger"} onClick={() => handleFilter("expired")}>
                        Expired
                    </Button>
                    {/* <Button variant={filterType === "expired" ? "danger" : "outline-danger"} onClick={() => extendExpiredMembershipsToApril30()}>
                        Renew
                    </Button> */}
                </div>

                {/* Search Bar */}
                <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Search by phone no..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                {/* Membership Table */}
                <div className="table-responsive fix-width scroll-inner">
                    <table className="table table-bordered table-hover">
                        <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
                            <tr>
                                <th>Sr. No</th>
                                <th>Phone No</th>
                                <th>Name</th>
                                <th>Membership Type</th>
                                <th>Price</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Status</th>
                                <th>User Type</th>
                                {/* <th>Delete</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {displayedData
                                // .filter(({ data }) => data.phone_no && data.phone_no.length === 10)
                                .map(({ id, data }, index) => {
                                    const isActive = moment(data.end_date.toDate()).isAfter(moment());
                                    const userType = userTypes[data.phone_no] || "N/A";

                                    return (
                                        <tr key={id}>
                                            <td>{index + 1}</td>
                                            <td>{data.phone_no}</td>
                                            <td>{personalDetails[data.phone_no] || "Loading..."}</td>
                                            <td>{data.membership_type}</td>
                                            <td>${data.price}</td>
                                            <td>{moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
                                            <td>{moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
                                            <td><b style={{ color: isActive ? "#28a745" : "salmon" }}>{isActive ? "Active" : "Expired"}</b></td>
                                            <td><b style={{ color: userType === "New" ? "#28a745" : "salmon" }}>{userType}</b></td>
                                            {/* <td>
                                                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(id)}>
                                                    Delete
                                                </button>
                                            </td> */}
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default MembershipTracking;


// import React, { useState, useEffect } from "react";


// import { Link } from "react-router-dom";
// import db from "./Firebase";
// import { collection, query, where, getDocs } from "firebase/firestore";
// import Moment from "moment";
// import { extendMoment } from "moment-range";
// import { Button } from "react-bootstrap";

// function MembershipTracking() {
//     const moment = extendMoment(Moment);
//     const [data, setData] = useState([]);
//     const [filteredData, setFilteredData] = useState([]);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [filterType, setFilterType] = useState("all");
//     const [userTypes, setUserTypes] = useState({}); // ✅ Stores precomputed "New" or "Old" values

//     useEffect(() => {
//         const unsubscribe = db
//             .collection("nv_glow_membership")
//             .orderBy("start_date", "desc")
//             .onSnapshot((snapshot) => {
//                 const memberships = snapshot.docs.map((doc) => ({
//                     id: doc.id,
//                     data: doc.data(),
//                 }));
//                 setData(memberships);
//                 setFilteredData(memberships);
//                 fetchPersonalDetails(memberships); // Fetch user details
//             });

//         return () => unsubscribe();
//     }, []);

//     // **Fetch personal details for all phone numbers**
//     const fetchPersonalDetails = async (memberships) => {
//         const phoneNumbers = [...new Set(memberships.map(({ data }) => data.phone_no))];

//         if (phoneNumbers.length === 0) return; // No phone numbers to fetch

//         let userTypeMap = {};
//         const batchSize = 10; // Firestore 'in' query limit
//         const cutoffDate = new Date("2024-01-15"); // January 15, 2024

//         for (let i = 0; i < phoneNumbers.length; i += batchSize) {
//             const batch = phoneNumbers.slice(i, i + batchSize);
//             const q = query(collection(db, "personal_details"), where("phone_no", "in", batch));

//             const snapshot = await getDocs(q);
//             snapshot.docs.forEach((doc) => {
//                 const d = doc.data();
//                 if (d.created_date) {
//                     const userCreatedDate = d.created_date.toDate ? d.created_date.toDate() : new Date(d.created_date);
//                     userTypeMap[d.phone_no] = userCreatedDate > cutoffDate ? "New" : "Old";
//                 } else {
//                     userTypeMap[d.phone_no] = "N/A"; // Handle missing data
//                 }
//             });
//         }

//         setUserTypes(userTypeMap); // ✅ Store computed values for quick access
//     };

//     // **Filter Data Based on Button Click**
//     const handleFilter = (type) => {
//         setFilterType(type);
//         const now = moment();

//         let filtered;
//         if (type === "active") {
//             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isAfter(now));
//         } else if (type === "expired") {
//             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isBefore(now));
//         } else {
//             filtered = data;
//         }

//         setFilteredData(filtered);
//     };

//     // **Filter data by search query**
//     const displayedData = filteredData.filter((record) =>
//         record.data.phone_no.includes(searchQuery)
//     );

//     return (
//         <>
//             <div className="p-2 d-flex justify-content-between">
//                 <Link to="/">
//                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
//                 </Link>
//             </div>

//             <div className="p-3">
//                 <div className="card-header pb-0 px-3">
//                     <h6 className="mb-2">
//                         Membership Tracking Page - |
//                         (Count: {displayedData.filter(({ data }) => data.phone_no && data.phone_no.length === 10).length})
//                     </h6>
//                 </div>

//                 {/* Filter Buttons */}
//                 <div className="mb-3 mt-3 d-flex gap-2">
//                     <Button variant={filterType === "all" ? "primary" : "outline-primary"} onClick={() => handleFilter("all")}>
//                         All
//                     </Button>
//                     <Button variant={filterType === "active" ? "success" : "outline-success"} onClick={() => handleFilter("active")}>
//                         Active
//                     </Button>
//                     <Button variant={filterType === "expired" ? "danger" : "outline-danger"} onClick={() => handleFilter("expired")}>
//                         Expired
//                     </Button>
//                 </div>

//                 {/* Search Bar */}
//                 <input
//                     type="text"
//                     className="form-control form-control-sm mb-2"
//                     placeholder="Search by phone no..."
//                     value={searchQuery}
//                     onChange={(e) => setSearchQuery(e.target.value)}
//                 />

//                 {/* Membership Table */}
//                 <div className="table-responsive fix-width scroll-inner">
//                     <table className="table table-bordered table-hover">
//                         <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
//                             <tr>
//                                 <th>Sr. No</th>
//                                 <th>Phone No</th>
//                                 <th>Membership Type</th>
//                                 <th>Price</th>
//                                 <th>Start Date</th>
//                                 <th>End Date</th>
//                                 <th>Status</th>
//                                 <th>User Type</th>
//                             </tr>
//                         </thead>
//                         <tbody>
//                             {displayedData
//                                 .filter(({ data }) => data.phone_no && data.phone_no.length === 10) // ✅ Only show 10-digit numbers
//                                 .map(({ id, data }, index) => {
//                                     const now = moment();
//                                     const endDate = moment(data.end_date.toDate());
//                                     const isActive = endDate.isAfter(now);
//                                     const userType = userTypes[data.phone_no] || "N/A"; // ✅ Fast access, no recomputation

//                                     return (
//                                         <tr key={id}>
//                                             <td>{index + 1}</td>
//                                             <td>{data.phone_no}</td>
//                                             <td>{data.membership_type}</td>
//                                             <td>${data.price}</td>
//                                             <td>{moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
//                                             <td>{moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
//                                             <td style={{ color: isActive ? "#28a745" : "salmon" }}><b>{isActive ? "Active" : "Expired"}</b></td>
//                                             <td style={{ color: userType == "New" ? "#28a745" : "salmon" }}><b>{userType}</b></td>
//                                         </tr>
//                                     );
//                                 })}
//                         </tbody>
//                     </table>
//                 </div>
//             </div>
//         </>
//     );
// }

// export default MembershipTracking;


// // import React, { useState, useEffect } from "react";


// // import { Link } from "react-router-dom";
// // import Swal from "sweetalert2";
// // import db from "./Firebase";
// // import { collection, query, where, getDocs } from "firebase/firestore";
// // import Moment from "moment";
// // import { extendMoment } from "moment-range";
// // import { Button } from "react-bootstrap";

// // function MembershipTracking() {
// //     const moment = extendMoment(Moment);
// //     const [data, setData] = useState([]);
// //     const [filteredData, setFilteredData] = useState([]);
// //     const [searchQuery, setSearchQuery] = useState("");
// //     const [filterType, setFilterType] = useState("all");
// //     const [personalDetails, setPersonalDetails] = useState({});

// //     useEffect(() => {
// //         const unsubscribe = db
// //             .collection("nv_glow_membership")
// //             .orderBy("start_date", "desc")
// //             .onSnapshot((snapshot) => {
// //                 const memberships = snapshot.docs.map((doc) => ({
// //                     id: doc.id,
// //                     data: doc.data(),
// //                 }));
// //                 setData(memberships);
// //                 setFilteredData(memberships);
// //                 fetchPersonalDetails(memberships); // Fetch user details
// //             });

// //         return () => unsubscribe();
// //     }, []);

// //     // **Fetch personal details for all phone numbers**
// //     const fetchPersonalDetails = async (memberships) => {
// //         const phoneNumbers = [...new Set(memberships.map(({ data }) => data.phone_no))];

// //         if (phoneNumbers.length === 0) return; // No phone numbers to fetch

// //         let detailsMap = {};
// //         const batchSize = 10; // Firestore 'in' query limit

// //         for (let i = 0; i < phoneNumbers.length; i += batchSize) {
// //             const batch = phoneNumbers.slice(i, i + batchSize);
// //             const q = query(collection(db, "personal_details"), where("phone_no", "in", batch));

// //             const snapshot = await getDocs(q);
// //             snapshot.docs.forEach((doc) => {
// //                 const d = doc.data();
// //                 detailsMap[d.phone_no] = d.created_date || null; // Store timestamp or null
// //             });
// //         }

// //         setPersonalDetails(detailsMap);
// //     };

// //     // **Determine if a user is "New" or "Old"**
// //     const getUserType = (phoneNo) => {
// //         const createdDate = personalDetails[phoneNo];
// //         if (!createdDate) return "N/A"; // If no data found

// //         const userCreatedDate = createdDate.toDate ? createdDate.toDate() : new Date(createdDate);
// //         const cutoffDate = new Date("2024-01-15"); // January 15, 2024

// //         return userCreatedDate > cutoffDate ? "New" : "Old";
// //     };

// //     // **Filter Data Based on Button Click**
// //     const handleFilter = (type) => {
// //         setFilterType(type);
// //         const now = moment();

// //         let filtered;
// //         if (type === "active") {
// //             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isAfter(now));
// //         } else if (type === "expired") {
// //             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isBefore(now));
// //         } else {
// //             filtered = data;
// //         }

// //         setFilteredData(filtered);
// //     };


// //     const handleDelete = (id) => {
// //         Swal.fire({
// //             title: "Are you sure?",
// //             text: "This record will be permanently deleted!",
// //             icon: "warning",
// //             showCancelButton: true,
// //             confirmButtonColor: "#d33",
// //             cancelButtonColor: "#3085d6",
// //             confirmButtonText: "Yes, delete it!"
// //         }).then((result) => {
// //             if (result.isConfirmed) {
// //                 db.collection("nv_glow_membership").doc(id).delete()
// //                     .then(() => {
// //                         Swal.fire("Deleted!", "The record has been deleted.", "success");
// //                     })
// //                     .catch((error) => {
// //                         Swal.fire("Error!", "Something went wrong.", "error");
// //                     });
// //             }
// //         });
// //     };

// //     // **Filter data by search query**
// //     const displayedData = filteredData.filter((record) =>
// //         record.data.phone_no.includes(searchQuery)
// //     );

// //     return (
// //         <>
// //             <div className="p-2 d-flex justify-content-between">
// //                 <Link to="/">
// //                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
// //                 </Link>
// //             </div>

// //             <div className="p-3">
// //                 <div className="card-header pb-0 px-3">
// //                     {/* <h6 className="mb-2">Membership Tracking Page - (Count: {displayedData.length})</h6> */}
// //                     <h6 className="mb-2">
// //                         Membership Tracking Page -  |
// //                         (Count: {displayedData.filter(({ data }) => data.phone_no && data.phone_no.length === 10).length})
// //                     </h6>

// //                 </div>

// //                 {/* Filter Buttons */}
// //                 <div className="mb-3 mt-3 d-flex gap-2">
// //                     <Button variant={filterType === "all" ? "primary" : "outline-primary"} onClick={() => handleFilter("all")}>
// //                         All
// //                     </Button>
// //                     <Button variant={filterType === "active" ? "success" : "outline-success"} onClick={() => handleFilter("active")}>
// //                         Active
// //                     </Button>
// //                     <Button variant={filterType === "expired" ? "danger" : "outline-danger"} onClick={() => handleFilter("expired")}>
// //                         Expired
// //                     </Button>
// //                 </div>

// //                 {/* Search Bar */}
// //                 <input
// //                     type="text"
// //                     className="form-control form-control-sm mb-2"
// //                     placeholder="Search by phone no..."
// //                     value={searchQuery}
// //                     onChange={(e) => setSearchQuery(e.target.value)}
// //                 />

// //                 {/* Membership Table */}
// //                 <div className="table-responsive fix-width scroll-inner">
// //                     <table className="table table-bordered table-hover">
// //                         <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
// //                             <tr>
// //                                 <th>Sr. No</th>
// //                                 <th>Phone No</th>
// //                                 <th>Membership Type</th>
// //                                 <th>Price</th>
// //                                 <th>Start Date</th>
// //                                 <th>End Date</th>
// //                                 <th>Status</th>
// //                                 <th>User Type</th>
// //                                 {/* <th>Delete</th> */}
// //                             </tr>
// //                         </thead>
// //                         <tbody>
// //                             {displayedData
// //                                 .filter(({ data }) => data.phone_no && data.phone_no.length === 10) // ✅ Only show 10-digit numbers
// //                                 .map(({ id, data }, index) => {
// //                                     const now = moment();
// //                                     const endDate = moment(data.end_date.toDate());
// //                                     const isActive = endDate.isAfter(now);
// //                                     const userType = getUserType(data.phone_no);

// //                                     return (
// //                                         <tr key={id}>
// //                                             <td>{index + 1}</td>
// //                                             <td>{data.phone_no}</td>
// //                                             <td>{data.membership_type}</td>
// //                                             <td>${data.price}</td>
// //                                             <td>{moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// //                                             <td>{moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// //                                             <td style={{ color: isActive ? "#28a745" : "salmon" }}><b>{isActive ? "Active" : "Expired"}</b></td>
// //                                             <td><b>{userType}</b></td>
// //                                             {/* <td>
// //                                                 <button className="btn btn-danger btn-sm" onClick={() => handleDelete(id)}>
// //                                                     Delete
// //                                                 </button>
// //                                             </td> */}
// //                                         </tr>
// //                                     );
// //                                 })}

// //                             {/* {displayedData.map(({ id, data }, index) => {
// //                                 const now = moment();
// //                                 const endDate = moment(data.end_date.toDate());
// //                                 const isActive = endDate.isAfter(now);
// //                                 const userType = getUserType(data.phone_no);

// //                                 return (
// //                                     <tr key={id}>
// //                                         <td>{index + 1}</td>
// //                                         <td>{data.phone_no}</td>
// //                                         <td>{data.membership_type}</td>
// //                                         <td>${data.price}</td>
// //                                         <td>{moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// //                                         <td>{moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// //                                         <td style={{ color: isActive ? "#28a745" : "salmon" }}><b>{isActive ? "Active" : "Expired"}</b></td>
// //                                         <td><b>{userType}</b></td>
// //                                         <td>
// //                                             <button className="btn btn-danger btn-sm" onClick={() => handleDelete(id)}>
// //                                                 Delete
// //                                             </button>
// //                                         </td>
// //                                     </tr>
// //                                 );
// //                             })} */}
// //                         </tbody>
// //                     </table>
// //                 </div>
// //             </div>
// //         </>
// //     );
// // }

// // export default MembershipTracking;




// // // import React, { useState, useEffect } from "react";


// // // import { Link } from "react-router-dom";
// // // import Swal from "sweetalert2";
// // // import db from "./Firebase";
// // // import Moment from "moment";
// // // import { extendMoment } from "moment-range";
// // // import { Button } from "react-bootstrap";

// // // function MembershipTracking() {
// // //     const moment = extendMoment(Moment);
// // //     const [data, setData] = useState([]);
// // //     const [filteredData, setFilteredData] = useState([]); // Data after filtering
// // //     const [searchQuery, setSearchQuery] = useState("");
// // //     const [filterType, setFilterType] = useState("all"); // 'all', 'active', 'expired'

// // //     useEffect(() => {
// // //         const unsubscribe = db
// // //             .collection("nv_glow_membership")
// // //             .orderBy("start_date", "desc")
// // //             .onSnapshot((snapshot) => {
// // //                 const memberships = snapshot.docs.map((doc) => ({
// // //                     id: doc.id,
// // //                     data: doc.data(),
// // //                 }));
// // //                 setData(memberships);
// // //                 setFilteredData(memberships); // Initialize filtered data
// // //             });

// // //         return () => unsubscribe();
// // //     }, []);


// // //     // **Handle Search**
// // //     const handleSearch = (e) => {
// // //         setSearchQuery(e.target.value);
// // //     };

// // //     // **Filter Data Based on Button Click**
// // //     const handleFilter = (type) => {
// // //         setFilterType(type);
// // //         const now = moment(); // Current time

// // //         let filtered;
// // //         if (type === "active") {
// // //             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isAfter(now));
// // //         } else if (type === "expired") {
// // //             filtered = data.filter((record) => moment(record.data.end_date.toDate()).isBefore(now));
// // //         } else {
// // //             filtered = data; // Show all
// // //         }

// // //         setFilteredData(filtered);
// // //     };

// // //     // **Filter data by search query**
// // //     const displayedData = filteredData.filter((record) =>
// // //         record.data.phone_no.includes(searchQuery)
// // //     );

// // //     return (
// // //         <>
// // //             <div className="p-2 d-flex justify-content-between">
// // //                 <Link to="/">
// // //                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
// // //                 </Link>
// // //             </div>

// // //             <div className="p-3">
// // //                 <div className="card-header pb-0 px-3">
// // //                     <h6 className="mb-2">Membership Tracking Page - (Count: {displayedData.length})</h6>
// // //                 </div>

// // //                 {/* Filter Buttons */}
// // //                 <div className="mb-3 mt-3 d-flex gap-2">
// // //                     <Button variant={filterType === "all" ? "primary" : "outline-primary"} onClick={() => handleFilter("all")}>
// // //                         All
// // //                     </Button>
// // //                     <Button variant={filterType === "active" ? "success" : "outline-success"} onClick={() => handleFilter("active")}>
// // //                         Active
// // //                     </Button>
// // //                     <Button variant={filterType === "expired" ? "danger" : "outline-danger"} onClick={() => handleFilter("expired")}>
// // //                         Expired
// // //                     </Button>
// // //                 </div>

// // //                 {/* Search Bar */}
// // //                 <input
// // //                     type="text"
// // //                     className="form-control form-control-sm mb-2"
// // //                     placeholder="Search by phone no..."
// // //                     value={searchQuery}
// // //                     onChange={handleSearch}
// // //                 />

// // //                 {/* Membership Table */}
// // //                 <div className="table-responsive fix-width scroll-inner">
// // //                     <table className="table table-bordered table-hover">
// // //                         <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
// // //                             <tr>
// // //                                 <th>Sr. No</th>
// // //                                 <th>Phone No</th>
// // //                                 <th>Membership Type</th>
// // //                                 <th>Price</th>
// // //                                 <th>Start Date</th>
// // //                                 <th>End Date</th>
// // //                                 <th>Status</th>
// // //                             </tr>
// // //                         </thead>
// // //                         <tbody>
// // //                             {displayedData.map(({ id, data }, index) => {
// // //                                 const now = moment();
// // //                                 const endDate = moment(data.end_date.toDate());
// // //                                 const isActive = endDate.isAfter(now);

// // //                                 return (
// // //                                     <tr key={id} >
// // //                                         <td>{index + 1}</td>
// // //                                         <td>{data.phone_no}</td>
// // //                                         <td>{data.membership_type}</td>
// // //                                         <td>{data.price}</td>
// // //                                         <td>{moment(data.start_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// // //                                         <td>{moment(data.end_date.toDate()).format("DD/MM/YY, h:mm a")}</td>
// // //                                         <td style={{ color: isActive ? "#28a745" : "salmon" }}><b>{isActive ? "Active" : "Expired"}</b></td>
// // //                                     </tr>
// // //                                 );
// // //                             })}
// // //                         </tbody>
// // //                     </table>
// // //                 </div>
// // //             </div>
// // //         </>
// // //     );
// // // }

// // // export default MembershipTracking;




// // // // import React, { useState, useEffect } from "react";


// // // // import { useNavigate, Link } from 'react-router-dom';
// // // // import Swal from 'sweetalert2';
// // // // import db from "./Firebase";
// // // // import Moment from 'moment';
// // // // import { extendMoment } from 'moment-range';

// // // // function MembershipTracking() {
// // // //     const moment = extendMoment(Moment);
// // // //     const [data, setData] = useState([]);
// // // //     const [searchQuery, setSearchQuery] = useState("");

// // // //     useEffect(() => {
// // // //         const unsubscribe = db.collection("nv_glow_membership")
// // // //             .orderBy("start_date", "desc")
// // // //             .onSnapshot((snapshot) => {
// // // //                 setData(snapshot.docs.map((doc) => ({
// // // //                     id: doc.id,
// // // //                     data: doc.data(),
// // // //                 })));
// // // //             });

// // // //         return () => unsubscribe();
// // // //     }, []);

// // // //     const handleSearch = (e) => {
// // // //         setSearchQuery(e.target.value);
// // // //     };

// // // //     const handleDelete = (id) => {
// // // //         Swal.fire({
// // // //             title: "Are you sure?",
// // // //             text: "This record will be permanently deleted!",
// // // //             icon: "warning",
// // // //             showCancelButton: true,
// // // //             confirmButtonColor: "#d33",
// // // //             cancelButtonColor: "#3085d6",
// // // //             confirmButtonText: "Yes, delete it!"
// // // //         }).then((result) => {
// // // //             if (result.isConfirmed) {
// // // //                 db.collection("nv_glow_membership").doc(id).delete()
// // // //                     .then(() => {
// // // //                         Swal.fire("Deleted!", "The record has been deleted.", "success");
// // // //                     })
// // // //                     .catch((error) => {
// // // //                         Swal.fire("Error!", "Something went wrong.", "error");
// // // //                     });
// // // //             }
// // // //         });
// // // //     };

// // // //     const filteredData = data.filter((record) =>
// // // //         record.data.phone_no.includes(searchQuery)
// // // //     );

// // // //     return (
// // // //         <>
// // // //             <div className="p-2" style={{ paddingBottom: "5px", display: "flex", justifyContent: "space-between" }}>
// // // //                 <Link to="/">
// // // //                     <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
// // // //                 </Link>
// // // //             </div>

// // // //             <div className="p-3">
// // // //                 <div className="card-header pb-0 px-3">
// // // //                     <h6 className="mb-2">Membership Tracking Page - (Count: {data.length})</h6>
// // // //                 </div>

// // // //                 <input
// // // //                     type="text"
// // // //                     className="form-control form-control-sm mb-2"
// // // //                     placeholder="Search by phone no..."
// // // //                     value={searchQuery}
// // // //                     onChange={handleSearch}
// // // //                 />

// // // //                 <div className="table-responsive fix-width scroll-inner">
// // // //                     <table className="table table-bordered table-hover">
// // // //                         <thead className="thead-dark" style={{ position: "sticky", top: "0" }}>
// // // //                             <tr>
// // // //                                 <th>Sr. No</th>
// // // //                                 <th>Phone No</th>
// // // //                                 <th>Membership Type</th>
// // // //                                 <th>Price</th>
// // // //                                 <th>Start Date</th>
// // // //                                 <th>End Date</th>
// // // //                                 {/* <th>Action</th> */}
// // // //                             </tr>
// // // //                         </thead>
// // // //                         <tbody>
// // // //                             {filteredData.map(({ id, data }, index) => (
// // // //                                 <tr key={id}>
// // // //                                     <td>{index + 1}</td>
// // // //                                     <td>{data.phone_no}</td>
// // // //                                     <td>{data.membership_type}</td>
// // // //                                     <td>{data.price}</td>
// // // //                                     <td>{moment(data.start_date.toDate().toISOString()).format("DD/MM/YY, h:mm a")}</td>
// // // //                                     <td>{moment(data.end_date.toDate().toISOString()).format("DD/MM/YY, h:mm a")}</td>
// // // //                                     {/* <td>
// // // //                                         <button className="btn btn-danger btn-sm" onClick={() => handleDelete(id)}>
// // // //                                             Delete
// // // //                                         </button>
// // // //                                     </td> */}
// // // //                                 </tr>
// // // //                             ))}
// // // //                         </tbody>
// // // //                     </table>
// // // //                 </div>
// // // //             </div>
// // // //         </>
// // // //     );
// // // // }

// // // // export default MembershipTracking;


// // // // // import React, { useState, useEffect } from "react";


// // // // // import { useNavigate, Link, useParams } from 'react-router-dom';
// // // // // import Swal from 'sweetalert2'
// // // // // import db from "./Firebase";
// // // // // import { storage } from './Firebase';
// // // // // import { ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
// // // // // import Moment from 'moment';
// // // // // import { extendMoment } from 'moment-range';

// // // // // function MembershipTracking() {

// // // // //     const moment = extendMoment(Moment);
// // // // //     const [data, setData] = useState([]);
// // // // //     const [searchQuery, setSearchQuery] = useState("");


// // // // //     useEffect(() => {
// // // // //         db.collection("nv_glow_membership").orderBy("start_date", "desc").onSnapshot((snapshot) => {
// // // // //             setData(
// // // // //                 snapshot.docs.map((doc) => ({
// // // // //                     id: doc.id,
// // // // //                     data: doc.data(),
// // // // //                 }))
// // // // //             );
// // // // //         });

// // // // //     }, []);

// // // // //     // Step 3: Filter data based on search query and availability filter
// // // // //     const filteredData = data.filter((record) => {
// // // // //         return (
// // // // //             record.data.phone_no.includes(searchQuery)
// // // // //         );
// // // // //     });



// // // // //     const handleSearch = (e) => {
// // // // //         setSearchQuery(e.target.value); // Step 3: Update search query state
// // // // //     }


// // // // //     return (
// // // // //         <>
// // // // //             <div class="p-2" style={{ paddingBottom: "5px", display: "flex", justifyContent: "space-between" }}>
// // // // //                 {/* <span class="btn bg-gradient-secondary btn-sm mb-0 mr-2" style={{ color: "white" }} >&nbsp; Download Excel</span> */}
// // // // //                 <Link to="/"><img src="/images/menubutton.png" class="menu-toggle" alt="Responsive image" style={{ height: "2rem" }} /></Link>

// // // // //             </div>

// // // // //             <div class="p-3">

// // // // //                 <div class="card-header pb-0 px-3">
// // // // //                     <h6 class="mb-2">Membership Tracking Page - (Count : {data.length})</h6>
// // // // //                 </div>
// // // // //                 <input
// // // // //                     type="text"
// // // // //                     className="form-control form-control-sm mb-2"
// // // // //                     placeholder="Search by phone no..."
// // // // //                     value={searchQuery}
// // // // //                     onChange={handleSearch}
// // // // //                 />


// // // // //                 <div class="table-responsive fix-width scroll-inner">
// // // // //                     <table class="table table-bordered table-hover">
// // // // //                         <thead class="thead-dark" style={{ position: "sticky", top: "0" }}>
// // // // //                             <tr>
// // // // //                                 <th>Sr. No</th>
// // // // //                                 <th>Phone No</th>
// // // // //                                 <th>Membership Type</th>
// // // // //                                 <th>Price</th>
// // // // //                                 <th>Start Date</th>
// // // // //                                 <th>End Date</th>
// // // // //                             </tr>
// // // // //                         </thead>
// // // // //                         <tbody>
// // // // //                             {filteredData?.map(({ id, data }, index) => (
// // // // //                                 <>
// // // // //                                     <tr>
// // // // //                                         <td>{index + 1}</td>
// // // // //                                         <td>{data.phone_no}</td>
// // // // //                                         <td>{data.membership_type}</td>
// // // // //                                         <td>{data.price}</td>
// // // // //                                         <td>{moment(data.start_date.toDate().toISOString()).format("DD/MM/YY, h:mm a")}</td>
// // // // //                                         <td>{moment(data.end_date.toDate().toISOString()).format("DD/MM/YY, h:mm a")}</td>
// // // // //                                     </tr>
// // // // //                                 </>
// // // // //                             ))}


// // // // //                         </tbody>
// // // // //                     </table>
// // // // //                 </div>
// // // // //             </div>



// // // // //         </>
// // // // //     )
// // // // // }

// // // // // export default MembershipTracking